import React, { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Trash2, Plus, Search, Scan, FileText, Printer, X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GST_RATE = 0.15;

export default function CustomerInvoiceDialog({ open, onOpenChange, getAuthHeader, stores = [], businessName = 'SupplyTrack' }) {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [customer, setCustomer] = useState({ name: '', address: '', phone: '', email: '' });
  const [invoiceNo, setInvoiceNo] = useState(() => `INV-${Date.now().toString().slice(-7)}`);
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    d.setDate(20);
    return d.toISOString().slice(0, 10);
  });
  const [paymentStatus, setPaymentStatus] = useState('unpaid');  // paid / unpaid / partial
  const [paidAmount, setPaidAmount] = useState('');
  const [takeStock, setTakeStock] = useState(false);
  const [stockStore, setStockStore] = useState('orewa');
  const [notes, setNotes] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [lines, setLines] = useState([]);  // {product_id, name, barcode, qty, unit_price}
  const [saving, setSaving] = useState(false);
  const scanRef = useRef(null);

  // Reset on close & fetch own product list on open
  useEffect(() => {
    if (!open) {
      setLines([]);
      setCustomer({ name: '', address: '', phone: '', email: '' });
      setInvoiceNo(`INV-${Date.now().toString().slice(-7)}`);
      setSearchQuery('');
      setScanInput('');
      setNotes('');
      setPaymentStatus('unpaid');
      setPaidAmount('');
      setTakeStock(false);
      return;
    }
    // Fetch real products from /api/products (independent of parent state)
    let cancelled = false;
    setProductsLoading(true);
    axios.get(`${API_URL}/products?limit=15000`, { headers: getAuthHeader() })
      .then(res => { if (!cancelled) setProducts(res.data || []); })
      .catch(err => { if (!cancelled) toast.error('Failed to load products: ' + (err?.response?.data?.detail || err.message)); })
      .finally(() => { if (!cancelled) setProductsLoading(false); });
    return () => { cancelled = true; };
  }, [open, getAuthHeader]);

  // Search results
  const searchResults = useMemo(() => {
    const term = (searchQuery || '').toLowerCase().trim();
    if (!term || term.length < 2) return [];
    const words = term.split(/\s+/).filter(Boolean);
    return products.filter(p => {
      const hay = `${p.name || ''} ${p.brand || ''} ${p.barcode || ''}`.toLowerCase();
      return words.every(w => hay.includes(w));
    }).slice(0, 8);
  }, [products, searchQuery]);

  const addLine = (product) => {
    const existing = lines.find(l => l.product_id === product.id);
    if (existing) {
      setLines(lines.map(l => l.product_id === product.id ? { ...l, qty: l.qty + 1 } : l));
    } else {
      setLines([...lines, {
        product_id: product.id,
        name: product.name,
        barcode: product.barcode || '',
        qty: 1,
        unit_price: Number(product.sell_price || 0),
      }]);
    }
    setSearchQuery('');
    setScanInput('');
    scanRef.current?.focus();
  };

  const updateLine = (idx, patch) => {
    setLines(lines.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  // Scan handler — called on Enter inside the scan input
  const handleScan = (code) => {
    code = (code || '').trim();
    if (!code) return;
    const stripLead = (s) => (s || '').replace(/\s+/g, '').replace(/^0+/, '');
    const match = products.find(p => {
      const pb = (p.barcode || '').replace(/\s+/g, '');
      return pb === code || stripLead(pb) === stripLead(code);
    });
    if (match) {
      addLine(match);
    } else {
      toast.error(`No product found for barcode "${code}"`);
      setScanInput('');
    }
  };

  // Totals (retail prices = GST-inclusive in NZ, common practice)
  const totals = useMemo(() => {
    const subtotalIncGst = lines.reduce((sum, l) => sum + (l.qty * l.unit_price), 0);
    const subtotalExGst = subtotalIncGst / (1 + GST_RATE);
    const gst = subtotalIncGst - subtotalExGst;
    return {
      subtotalExGst: Math.round(subtotalExGst * 100) / 100,
      gst: Math.round(gst * 100) / 100,
      total: Math.round(subtotalIncGst * 100) / 100,
    };
  }, [lines]);

  const generatePDF = () => {
    if (!customer.name.trim()) { toast.error('Customer name is required'); return; }
    if (lines.length === 0) { toast.error('Add at least one product'); return; }

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = 210;
    let y = 15;

    // Header
    doc.setFontSize(22).setFont(undefined, 'bold');
    doc.text('TAX INVOICE', pageWidth / 2, y, { align: 'center' });
    y += 10;

    // From / To row
    doc.setFontSize(9).setFont(undefined, 'bold');
    doc.text('FROM', 14, y);
    doc.text('BILL TO', 110, y);
    y += 4;
    doc.setFont(undefined, 'normal');
    doc.text(businessName, 14, y);
    doc.text(customer.name, 110, y);
    y += 4;
    doc.text(stores.map(s => s.name).join(' & ') || 'New Zealand', 14, y);
    if (customer.address) {
      const lines2 = doc.splitTextToSize(customer.address, 85);
      doc.text(lines2, 110, y);
      y += 4 * lines2.length;
    } else {
      y += 4;
    }
    if (customer.phone) { doc.text(`Phone: ${customer.phone}`, 110, y); y += 4; }
    if (customer.email) { doc.text(`Email: ${customer.email}`, 110, y); y += 4; }

    y += 4;
    // Invoice meta box
    doc.setDrawColor(180);
    doc.rect(14, y, pageWidth - 28, 18);
    doc.setFont(undefined, 'bold').setFontSize(8.5);
    doc.text('INVOICE #', 18, y + 5);
    doc.text('DATE', 70, y + 5);
    doc.text('DUE DATE', 110, y + 5);
    doc.text('STATUS', 160, y + 5);
    doc.setFont(undefined, 'normal').setFontSize(11);
    doc.text(invoiceNo, 18, y + 12);
    doc.text(new Date(invoiceDate).toLocaleDateString('en-NZ'), 70, y + 12);
    doc.text(new Date(dueDate).toLocaleDateString('en-NZ'), 110, y + 12);
    const statusLabel = paymentStatus === 'paid' ? 'PAID' : paymentStatus === 'partial' ? 'PART PAID' : 'UNPAID';
    const statusColor = paymentStatus === 'paid' ? [22, 163, 74] : paymentStatus === 'partial' ? [217, 119, 6] : [220, 38, 38];
    doc.setTextColor(...statusColor).setFont(undefined, 'bold');
    doc.text(statusLabel, 160, y + 12);
    doc.setTextColor(0, 0, 0);

    y += 24;

    // Table
    autoTable(doc, {
      startY: y,
      head: [['#', 'Product', 'Barcode', 'Qty', 'Unit (inc GST)', 'Line Total']],
      body: lines.map((l, i) => [
        String(i + 1),
        l.name,
        l.barcode || '-',
        String(l.qty),
        `$${l.unit_price.toFixed(2)}`,
        `$${(l.qty * l.unit_price).toFixed(2)}`,
      ]),
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },
        2: { cellWidth: 28, fontStyle: 'normal', textColor: [100, 116, 139] },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 28, halign: 'right' },
        5: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: 14, right: 14 },
    });

    let yAfter = doc.lastAutoTable.finalY + 6;

    // Totals box (right-aligned)
    const tx = 130;
    doc.setFontSize(10);
    doc.text('Subtotal (ex GST):', tx, yAfter);
    doc.text(`$${totals.subtotalExGst.toFixed(2)}`, pageWidth - 14, yAfter, { align: 'right' });
    yAfter += 5;
    doc.text('GST (15%):', tx, yAfter);
    doc.text(`$${totals.gst.toFixed(2)}`, pageWidth - 14, yAfter, { align: 'right' });
    yAfter += 5;
    doc.setLineWidth(0.4).line(tx, yAfter, pageWidth - 14, yAfter);
    yAfter += 5;
    doc.setFontSize(13).setFont(undefined, 'bold');
    doc.text('TOTAL DUE:', tx, yAfter);
    doc.text(`$${totals.total.toFixed(2)}`, pageWidth - 14, yAfter, { align: 'right' });
    yAfter += 8;

    if (paymentStatus === 'partial' && paidAmount) {
      doc.setFontSize(10).setFont(undefined, 'normal');
      const paid = Number(paidAmount) || 0;
      const balance = totals.total - paid;
      doc.text(`Paid: $${paid.toFixed(2)}`, tx, yAfter); yAfter += 5;
      doc.setFont(undefined, 'bold').text(`Balance: $${balance.toFixed(2)}`, tx, yAfter); yAfter += 6;
    }

    if (notes) {
      doc.setFontSize(9).setFont(undefined, 'bold');
      doc.text('Notes:', 14, yAfter); yAfter += 4;
      doc.setFont(undefined, 'normal');
      const noteLines = doc.splitTextToSize(notes, pageWidth - 28);
      doc.text(noteLines, 14, yAfter);
    }

    // Footer
    doc.setFontSize(8).setTextColor(120);
    doc.text('Thank you for your business.', pageWidth / 2, 285, { align: 'center' });

    doc.save(`${invoiceNo}_${customer.name.replace(/\s+/g, '_')}.pdf`);
  };

  const saveAndGenerate = async () => {
    if (!customer.name.trim()) { toast.error('Customer name is required'); return; }
    if (lines.length === 0) { toast.error('Add at least one product'); return; }

    setSaving(true);
    try {
      const payload = {
        invoice_no: invoiceNo,
        invoice_date: invoiceDate,
        due_date: dueDate,
        customer_name: customer.name,
        customer_address: customer.address,
        customer_phone: customer.phone,
        customer_email: customer.email,
        payment_status: paymentStatus,
        paid_amount: paymentStatus === 'partial' ? Number(paidAmount || 0) : (paymentStatus === 'paid' ? totals.total : 0),
        notes,
        take_stock: takeStock,
        stock_store: takeStock ? stockStore : null,
        items: lines.map(l => ({
          product_id: l.product_id,
          name: l.name,
          barcode: l.barcode,
          quantity: l.qty,
          unit_price: l.unit_price,
          line_total: Math.round(l.qty * l.unit_price * 100) / 100,
        })),
        subtotal_ex_gst: totals.subtotalExGst,
        gst: totals.gst,
        total: totals.total,
      };
      await axios.post(`${API_URL}/customer-invoices`, payload, { headers: getAuthHeader() });
      generatePDF();
      toast.success(`Invoice ${invoiceNo} saved${takeStock ? ' & stock deducted' : ''}`);
      onOpenChange(false);
    } catch (err) {
      // Even if save fails, still let them download the PDF
      generatePDF();
      toast.error('Saved on your device only — server save failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
            <FileText className="w-5 h-5" /> Customer Invoice (A4 PDF)
          </DialogTitle>
          <DialogDescription>
            Free-type the customer's details, scan/search products, generate a downloadable PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1">
          {/* Customer + Invoice meta */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  data-testid="invoice-customer-name"
                  value={customer.name}
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                  placeholder="e.g. Jane Smith"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Textarea
                  data-testid="invoice-customer-address"
                  rows={2}
                  value={customer.address}
                  onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                  placeholder="Street, Suburb, City, Postcode"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Phone</Label>
                  <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Invoice #</Label>
                  <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
                </div>
                <div>
                  <Label>Invoice Date</Label>
                  <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Due Date</Label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </div>
                <div>
                  <Label>Payment Status</Label>
                  <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="unpaid">Unpaid</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {paymentStatus === 'partial' && (
                <div>
                  <Label>Amount Paid</Label>
                  <Input type="number" step="0.01" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} placeholder="0.00" />
                </div>
              )}
              <div className="flex items-start gap-2 p-2 border border-amber-200 bg-amber-50 rounded">
                <input
                  type="checkbox"
                  id="take-stock-cb"
                  checked={takeStock}
                  onChange={(e) => setTakeStock(e.target.checked)}
                  className="mt-1"
                  data-testid="invoice-take-stock"
                />
                <div className="flex-1">
                  <label htmlFor="take-stock-cb" className="text-sm font-semibold text-amber-900 cursor-pointer">
                    Deduct stock from store
                  </label>
                  <p className="text-xs text-amber-700">If you've already physically given them the products. Otherwise leave OFF.</p>
                  {takeStock && (
                    <Select value={stockStore} onValueChange={setStockStore}>
                      <SelectTrigger className="mt-2 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {stores.map(s => <SelectItem key={s.id || s.value || s.name} value={s.id || s.value || s.name.toLowerCase()}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Scan + Search */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Scan barcode {productsLoading && <span className="text-xs text-slate-400">(loading…)</span>}</Label>
              <div className="relative">
                <Scan className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  ref={scanRef}
                  className="pl-9"
                  placeholder="Scan or paste barcode + Enter"
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleScan(scanInput);
                    }
                  }}
                  data-testid="invoice-scan-input"
                />
              </div>
            </div>
            <div>
              <Label>Or search by name</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Type 2+ characters..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="invoice-search-input"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="absolute z-50 mt-1 w-full max-w-md bg-white border rounded shadow-lg max-h-64 overflow-y-auto">
                  {searchResults.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-0 text-sm"
                      onClick={() => addLine(p)}
                    >
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-slate-500">${(p.sell_price || 0).toFixed(2)} · {p.barcode || 'no barcode'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Lines */}
          <div className="border rounded">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left">
                  <th className="p-2 w-10">#</th>
                  <th className="p-2">Product</th>
                  <th className="p-2 w-24 text-center">Qty</th>
                  <th className="p-2 w-32 text-right">Unit Price</th>
                  <th className="p-2 w-28 text-right">Line Total</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-400">No products added — scan or search above</td></tr>
                ) : lines.map((l, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="p-2 text-slate-400">{i + 1}</td>
                    <td className="p-2">
                      <div className="font-medium">{l.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{l.barcode || '-'}</div>
                    </td>
                    <td className="p-2">
                      <Input type="number" min="1" value={l.qty} onChange={(e) => updateLine(i, { qty: Math.max(1, parseInt(e.target.value, 10) || 1) })} className="w-20 text-center mx-auto h-8" />
                    </td>
                    <td className="p-2 text-right">
                      <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: parseFloat(e.target.value) || 0 })} className="w-24 text-right ml-auto h-8" />
                    </td>
                    <td className="p-2 text-right font-semibold">${(l.qty * l.unit_price).toFixed(2)}</td>
                    <td className="p-2">
                      <Button size="sm" variant="ghost" onClick={() => removeLine(i)} className="text-red-600 h-7 w-7 p-0"><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-72 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal (ex GST):</span><span>${totals.subtotalExGst.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">GST (15%):</span><span>${totals.gst.toFixed(2)}</span></div>
              <div className="border-t pt-1 flex justify-between text-base font-bold"><span>TOTAL DUE:</span><span>${totals.total.toFixed(2)}</span></div>
              {paymentStatus === 'paid' && <Badge className="ml-auto bg-emerald-100 text-emerald-700">PAID</Badge>}
              {paymentStatus === 'unpaid' && <Badge className="ml-auto bg-red-100 text-red-700">UNPAID</Badge>}
              {paymentStatus === 'partial' && <Badge className="ml-auto bg-amber-100 text-amber-700">PARTIAL</Badge>}
            </div>
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any special notes for this invoice" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button variant="outline" onClick={generatePDF} data-testid="invoice-pdf-only-btn">
            <Printer className="w-4 h-4 mr-1" /> PDF only (don't save)
          </Button>
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={saveAndGenerate} disabled={saving} data-testid="invoice-save-and-pdf-btn">
            {saving ? 'Saving…' : (<><Plus className="w-4 h-4 mr-1" /> Save + Generate PDF</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
